import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Badge } from './ui/badge';
import { Pagination } from './ui/pagination';
import { 
  Plus, 
  Search, 
  Filter, 
  Download, 
  Eye, 
  Edit, 
  Trash2,
  Receipt,
  Calendar,
  DollarSign,
  Tag,
  FileText
} from 'lucide-react';
import { toast } from 'sonner';
import { 
  invoiceApiService, 
  ChargeMaster,
  ChargeMasterFilters
} from '../services/invoiceApi';
import { formatDate } from '../utils/dateUtils';

const ChargeMasterListingPage: React.FC = () => {
  const navigate = useNavigate();
  
  // State
  const [chargeMasters, setChargeMasters] = useState<ChargeMaster[]>([]);
  const [filteredChargeMasters, setFilteredChargeMasters] = useState<ChargeMaster[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalChargeMasters, setTotalChargeMasters] = useState(0);
  
  // Filters
  const [filters, setFilters] = useState<ChargeMasterFilters>({});
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [taxableFilter, setTaxableFilter] = useState<string>('');
  
  // Load charge masters
  const loadChargeMasters = useCallback(async () => {
    try {
      setLoading(true);
      const response = await invoiceApiService.getChargeMasters(currentPage, pageSize, filters);
      if (response.success) {
        setChargeMasters(response.data || []);
        setFilteredChargeMasters(response.data || []);
        // Assuming the API returns pagination info
        // setTotalPages(Math.ceil((response.pagination?.total || 0) / pageSize));
        // setTotalChargeMasters(response.pagination?.total || 0);
      }
    } catch (error) {
      // console.error('Error loading charge masters:', error);
      toast.error('Failed to load charge masters');
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, filters]);

  // Load charge masters on component mount and when filters change
  useEffect(() => {
    loadChargeMasters();
  }, [loadChargeMasters]);

  // Apply filters
  const applyFilters = useCallback(() => {
    let filtered = [...chargeMasters];
    
    // Apply search query
    if (searchQuery) {
      filtered = filtered.filter(charge => 
        charge.chargeCode?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        charge.chargeName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        charge.description?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    
    // Apply type filter
    if (typeFilter && typeFilter !== 'all') {
      filtered = filtered.filter(charge => charge.defaultType === typeFilter);
    }
    
    // Apply taxable filter
    if (taxableFilter !== '' && taxableFilter !== 'all') {
      const isTaxable = taxableFilter === 'true';
      filtered = filtered.filter(charge => charge.taxable === isTaxable);
    }
    
    setFilteredChargeMasters(filtered);
  }, [chargeMasters, searchQuery, typeFilter, taxableFilter]);

  // Apply filters when they change
  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  // Handle page change
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Handle search
  const handleSearch = (query: string) => {
    setSearchQuery(query);
  };

  // Handle type filter change
  const handleTypeFilterChange = (type: string) => {
    setTypeFilter(type);
  };

  // Handle taxable filter change
  const handleTaxableFilterChange = (taxable: string) => {
    setTaxableFilter(taxable);
  };

  // Handle view charge master
  const handleViewChargeMaster = (id: number) => {
    navigate(`/charge-masters/${id}/view`);
  };

  // Handle edit charge master
  const handleEditChargeMaster = (id: number) => {
    navigate(`/charge-masters/${id}/edit`);
  };

  // Handle delete charge master
  const handleDeleteChargeMaster = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this charge master?')) {
      try {
        await invoiceApiService.deleteChargeMaster(id.toString());
        toast.success('Charge master deleted successfully');
        loadChargeMasters(); // Reload the list
      } catch (error) {
        // console.error('Error deleting charge master:', error);
        toast.error('Failed to delete charge master');
      }
    }
  };

  // Get type badge color
  const getTypeBadgeColor = (type: string) => {
    switch (type) {
      case 'PERCENTAGE':
        return 'bg-blue-100 text-blue-800';
      case 'FIXED':
        return 'bg-green-100 text-green-800';
      case 'PER_UNIT':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Get amount type badge color
  const getAmountTypeBadgeColor = (amountType: string) => {
    switch (amountType) {
      case 'INVOICE_TOTAL':
        return 'bg-indigo-100 text-indigo-800';
      case 'LINE_ITEM':
        return 'bg-orange-100 text-orange-800';
      case 'FIXED_AMOUNT':
        return 'bg-pink-100 text-pink-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Export to CSV
  const exportToCSV = () => {
    const headers = [
      'Charge Code',
      'Charge Name',
      'Description',
      'Default Type',
      'Default Rate',
      'Default Amount Type',
      'Taxable',
      'Effective Start Date',
      'GL Account Code'
    ];
    
    const csvData = filteredChargeMasters.map(charge => [
      charge.chargeCode || '',
      charge.chargeName || '',
      charge.description || '',
      charge.defaultType || '',
      charge.defaultRate?.toString() || '',
      charge.defaultAmountType || '',
      charge.taxable ? 'Yes' : 'No',
      charge.effectiveStartDate || '',
      charge.glAccountCode || ''
    ]);
    
    const csvContent = [headers, ...csvData]
      .map(row => row.map(field => `"${field}"`).join(','))
      .join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `charge_masters_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Charge Master Management</h1>
          <p className="text-gray-600 mt-2">Manage and configure charge types for invoices</p>
        </div>
        <Button
          onClick={() => navigate('/charge-masters/new')}
          className="bg-primary-600 hover:bg-primary-700 text-white"
        >
          <Plus className="h-4 w-4 mr-2" />
          Create Charge Master
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Charges</CardTitle>
            <Receipt className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalChargeMasters}</div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Percentage Based</CardTitle>
            <Tag className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {chargeMasters.filter(charge => charge.defaultType === 'PERCENTAGE').length}
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Fixed Amount</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {chargeMasters.filter(charge => charge.defaultType === 'FIXED').length}
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Taxable Charges</CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {chargeMasters.filter(charge => charge.taxable).length}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters and Search */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-primary-700" />
            Filters & Search
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label htmlFor="search">Search</Label>
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <Input
                  id="search"
                  placeholder="Search by code, name, or description..."
                  value={searchQuery}
                  onChange={(e) => handleSearch(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="type">Type</Label>
              <Select value={typeFilter} onValueChange={handleTypeFilterChange}>
                <SelectTrigger>
                  <SelectValue placeholder="All Types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="PERCENTAGE">Percentage</SelectItem>
                  <SelectItem value="FIXED">Fixed</SelectItem>
                  <SelectItem value="PER_UNIT">Per Unit</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="taxable">Taxable</Label>
              <Select value={taxableFilter} onValueChange={handleTaxableFilterChange}>
                <SelectTrigger>
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="true">Taxable</SelectItem>
                  <SelectItem value="false">Non-Taxable</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex items-end">
              <Button
                onClick={exportToCSV}
                variant="outline"
                className="w-full"
              >
                <Download className="h-4 w-4 mr-2" />
                Export CSV
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Charge Masters Table */}
      <Card>
        <CardHeader>
          <CardTitle>Charge Master List</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
              <p className="mt-2 text-gray-600">Loading charge masters...</p>
            </div>
          ) : filteredChargeMasters.length > 0 ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gradient-to-r from-primary-50 to-primary-100/50">
                    <TableHead className="font-semibold">Charge Code</TableHead>
                    <TableHead className="font-semibold">Charge Name</TableHead>
                    <TableHead className="font-semibold">Description</TableHead>
                    <TableHead className="font-semibold">Default Type</TableHead>
                    <TableHead className="font-semibold">Default Rate</TableHead>
                    <TableHead className="font-semibold">Amount Type</TableHead>
                    <TableHead className="font-semibold">Taxable</TableHead>
                    <TableHead className="font-semibold">Effective Date</TableHead>
                    <TableHead className="font-semibold">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredChargeMasters.map((charge) => (
                    <TableRow key={charge.id} className="hover:bg-primary-50/50">
                      <TableCell>
                        <div className="font-medium text-gray-900">
                          {charge.chargeCode || 'N/A'}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="font-medium text-gray-900">
                          {charge.chargeName}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm text-gray-600 max-w-xs truncate">
                          {charge.description || 'N/A'}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={getTypeBadgeColor(charge.defaultType)}>
                          {charge.defaultType}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="font-medium text-gray-900">
                          {charge.defaultType === 'PERCENTAGE' 
                            ? `${charge.defaultRate}%` 
                            : `₹${charge.defaultRate?.toLocaleString() || '0'}`
                          }
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={getAmountTypeBadgeColor(charge.defaultAmountType)}>
                          {charge.defaultAmountType}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge className={charge.taxable ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                          {charge.taxable ? 'Yes' : 'No'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm text-gray-600">
                          {charge.effectiveStartDate ? formatDate(charge.effectiveStartDate) : 'N/A'}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleViewChargeMaster(charge.id)}
                            className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEditChargeMaster(charge.id)}
                            className="text-green-600 hover:text-green-700 hover:bg-green-50"
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteChargeMaster(charge.id)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500 bg-gradient-to-r from-primary-50 to-primary-100/50 rounded-lg border border-primary-200">
              <Receipt className="h-12 w-12 mx-auto text-gray-400 mb-4" />
              <p className="text-lg font-medium mb-2">No Charge Masters Found</p>
              <p className="text-sm mb-4">Try adjusting your search criteria or create a new charge master.</p>
              <Button
                onClick={() => navigate('/charge-masters/new')}
                className="bg-primary-600 hover:bg-primary-700 text-white"
              >
                <Plus className="h-4 w-4 mr-2" />
                Create Charge Master
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <Card>
          <CardContent className="pt-6">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ChargeMasterListingPage;
